import { computed, type ComputedRef } from 'vue'

import { useAdminApplicationStore } from '@/stores/admin/application'

export type AdminPermissionRequirement = string | readonly string[]

const normalizePermissions = (permissions: readonly string[]): string[] => {
  return permissions.map(permission => permission.trim()).filter(Boolean)
}

/**
 * Admin 权限只以服务端下发的精确权限字符串为准；多个权限同时传入时必须全部具备。
 * 该检查仅控制前端可见性，服务端鉴权仍是最终安全边界。
 */
export const hasAdminPermission = (
  grantedPermissions: readonly string[],
  requirement: AdminPermissionRequirement
): boolean => {
  const required = normalizePermissions(
    typeof requirement === 'string' ? [requirement] : requirement
  )

  if (!required.length) {
    return false
  }

  const granted = new Set(normalizePermissions(grantedPermissions))
  return required.every(permission => granted.has(permission))
}

export interface AdminPermissions {
  hasPermission: (requirement: AdminPermissionRequirement) => boolean
  permissions: ComputedRef<readonly string[]>
}

export const useAdminPermissions = (): AdminPermissions => {
  const applicationStore = useAdminApplicationStore()
  const permissions = computed<readonly string[]>(() => applicationStore.permissions)

  return {
    permissions,
    hasPermission: requirement => hasAdminPermission(permissions.value, requirement)
  }
}
